/**
 * Tier Evaluation Date Utilities
 * Calculates the next evaluation date based on the evaluation frequency
 * Ported from TierAutomationService.java logic
 */

/**
 * Maximum days to scan forward for the next evaluation date
 * Covers MONTHLY, WEEKLY, and BIWEEKLY frequencies
 */
const MAX_NEXT_EVALUATION_SCAN_DAYS = 62;

/**
 * Biweekly anchor date for parity calculation (1970-01-04 was a Sunday)
 * This anchor remains stable across year boundaries
 */
const BIWEEKLY_ANCHOR = new Date(1970, 0, 4); // January 4, 1970 (Sunday)

/**
 * Get the number of days between two dates
 * @param {Date} from - Start date
 * @param {Date} to - End date
 * @returns {number} Number of days between dates
 */
const getDaysBetween = (from, to) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((to - from) / msPerDay);
};

/**
 * Get the day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @param {Date} date - Date object
 * @returns {number} Day of week
 */
const getDayOfWeek = (date) => {
  return date.getUTCDay();
};

/**
 * Get the last day of the month for a given date
 * @param {Date} date - Date object (UTC)
 * @returns {number} Last day of month
 */
const getLastDayOfMonth = (date) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  // Create a date for the first day of next month, then go back 1 day
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
};

/**
 * Determine if a given date is an evaluation day based on frequency
 * @param {string} frequency - Evaluation frequency (MONTHLY, WEEKLY, BIWEEKLY)
 * @param {Date} date - Date to check
 * @returns {boolean} True if the date is an evaluation day for the frequency
 */
export const isEvaluationDay = (frequency, date) => {
  if (!frequency) {
    return true;
  }

  const dayOfWeek = getDayOfWeek(date);
  const dayOfMonth = date.getUTCDate();
  const lastDayOfMonth = getLastDayOfMonth(date);

  switch (frequency.toUpperCase()) {
    case 'MONTHLY':
      return dayOfMonth === lastDayOfMonth;

    case 'WEEKLY':
      return dayOfWeek === 0; // Sunday

    case 'BIWEEKLY': {
      // Check if it's a Sunday and on the correct fortnight
      if (dayOfWeek !== 0) {
        return false;
      }
      const daysDiff = getDaysBetween(BIWEEKLY_ANCHOR, date);
      return daysDiff % 14 === 0;
    }

    default:
      return true;
  }
};

/**
 * Calculate the next evaluation date from today (UTC)
 * Scans forward from today, at most MAX_NEXT_EVALUATION_SCAN_DAYS days,
 * for the next date on which the frequency's cadence evaluates.
 * @param {string} frequency - Evaluation frequency (MONTHLY, WEEKLY, BIWEEKLY)
 * @returns {string} Formatted date string (mm-dd-yyyy) or empty string if no date found
 */
export const calculateNextEvaluationDate = (frequency) => {
  if (!frequency) {
    return '';
  }

  // Start from today in UTC
  const today = new Date();
  const startDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );

  // Scan forward up to MAX_NEXT_EVALUATION_SCAN_DAYS days
  for (let offset = 0; offset < MAX_NEXT_EVALUATION_SCAN_DAYS; offset++) {
    const candidate = new Date(startDate);
    candidate.setUTCDate(candidate.getUTCDate() + offset);

    if (isEvaluationDay(frequency, candidate)) {
      // Format as mm-dd-yyyy
      const month = String(candidate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(candidate.getUTCDate()).padStart(2, '0');
      const year = candidate.getUTCFullYear();
      return `${month}-${day}-${year}`;
    }
  }

  // No evaluation date found within the scan limit
  return '';
};
